import * as React from 'react';
import { useState } from 'react';
import Head from 'next/head';
import Obfuscate from 'react-obfuscate';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import UserCheck from 'react-feather/dist/icons/user-check';
import Send from 'react-feather/dist/icons/send';
import Linkedin from 'react-feather/dist/icons/linkedin';
import Twitter from 'react-feather/dist/icons/twitter';
import Facebook from 'react-feather/dist/icons/facebook';
import Rss from 'react-feather/dist/icons/rss';
import ReactGA from 'react-ga';
import headTitle from "../ui/HeadTitle";
import OutboundLink from '../ui/OutboundLink';

const SendMessageModal = ({isOpen, toggle}: {isOpen: boolean, toggle: any}) => (
    <Modal isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Send Message</ModalHeader>
        <ModalBody>
            Unfortunately it's not possible to send a message to this speaker.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>
);

const FollowModal = ({isOpen, toggle}: {isOpen: boolean, toggle: any}) => (
    <Modal isOpen={isOpen} toggle={toggle}>
        <ModalHeader toggle={toggle}>Follow</ModalHeader>
        <ModalBody>
            Unfortunately it's not possible to follow this speaker right now.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>
);

const SpeakerDetail = ({speaker}) => {
    const [isSendMessageModalOpen, setSendMessageModalOpen] = useState(false);
    const toggleSendMessageModal = () => {
        if (!isSendMessageModalOpen) {
            ReactGA.event({
                category: 'Speaker',
                action: 'Send Message Click',
                label: speaker.name
            });
        }
        setSendMessageModalOpen(!isSendMessageModalOpen);
    };

    const [isFollowModalOpen, setFollowModalOpen] = useState(false);
    const toggleFollowModal = () => {
        if (!isFollowModalOpen) {
            ReactGA.event({
                category: 'Speaker',
                action: 'Follow Click',
                label: speaker.name
            });
        }
        setFollowModalOpen(!isFollowModalOpen);
    };

    const metaDescription = `Who is ${speaker.name}? Where can you meet ${speaker.name}? Find out more...`;
    return (
        <main>
            <Head>
                <title>{headTitle(speaker.name)}</title>
                <meta key="og:title" property="og:title" content={headTitle(speaker.name)} />
                <meta key="og:url" property="og:url" content={`https://aiml.events/speakers/${speaker.slug}`} />
                {speaker.image && <meta key="og:image" property="og:image" content={`https://aiml.events${speaker.image.image}`} />}
                <meta key="og:description" property="og:description" content={metaDescription} />
                <meta key="twitter:card" property="twitter:card" content="summary_large_image" />
                <meta key="twitter:title" property="twitter:title" content={headTitle(speaker.name)} />
                <meta key="twitter:description" property="twitter:description" content={metaDescription} />
                {speaker.image && <meta key="twitter:image" property="twitter:image" content={`https://aiml.events${speaker.image.image}`} />}
                <meta name="description" content={metaDescription} />
            </Head>
            <div className="bg-light">
                <div className="container space-1">
                    <div className="row">
                        <div className="col-lg-3 mb-7 mb-lg-0">
                            {/* Profile Card */}
                            <div className="card p-1 mb-4">
                                <div className="card-body text-center">
                                    <div className="mb-3">
                                        <img className="u-lg-avatar rounded-circle" src={speaker.image.imageCoverPreview} alt="Image Description" />
                                    </div>
                                    <div className="mb-3">
                                        <h1 className="h6 font-weight-medium mb-0">{speaker.name}</h1>
                                        <small className="d-block text-muted">Speaker</small>
                                    </div>
                                    <div className="mb-2">
                                        <Button className="btn btn-sm btn-block btn-soft-primary transition-3d-hover" onClick={toggleFollowModal}>
                                            <Rss size={20} className="mr-2" />
                                            Follow
                                        </Button>
                                    </div>
                                    <FollowModal isOpen={isFollowModalOpen} toggle={toggleFollowModal} />
                                    <div className="mb-2">
                                        <Button className="btn btn-sm btn-block btn-soft-secondary transition-3d-hover" onClick={toggleSendMessageModal}>
                                            <Send size={20} className="mr-2" />
                                            Send Message
                                        </Button>
                                    </div>
                                    <SendMessageModal isOpen={isSendMessageModalOpen} toggle={toggleSendMessageModal} />
                                    <Obfuscate className="text-secondary small"
                                        obfuscateChildren={false}
                                        email="info@aiml.events"
                                        headers={{ subject: `Update ${speaker.name} Profile` }}>
                                        Suggest an update
                                    </Obfuscate>
                                </div>
                            </div>
                            {/* End Profile Card */}

                            {/* Social Profiles */}
                            <div className="card mb-4">
                                <div className="card-header pt-4 pb-3 px-0 mx-4">
                                    <h3 className="h6 mb-0">Social Profiles</h3>
                                </div>
                                <div className="card-body pt-3 pb-4 px-4">
                                    {/* Social Profiles */}
                                    {speaker.linkedin && <OutboundLink className="media mb-4" eventLabel={`${speaker.name} - LinkedIn`} to={`https://www.linkedin.com/in/${speaker.linkedin}`} rel="nofollow noopener" target="_blank">
                                        <div className="u-sm-avatar mr-3">
                                            <Linkedin color="black" size={32} />
                                        </div>
                                        <div className="media-body">
                                            <span className="d-block text-dark">LinkedIn</span>
                                            <small className="d-block text-secondary">{speaker.linkedin}</small>
                                        </div>
                                    </OutboundLink>}
                                    {/* End Social Profiles */}
                                    {/* Social Profiles */}
                                    {speaker.twitter && <OutboundLink className="media mb-4" eventLabel={`${speaker.name} - Twitter`} to={`https://twitter.com/${speaker.twitter}`} rel="nofollow noopener" target="_blank">
                                        <div className="u-sm-avatar mr-3">
                                            <Twitter color="black" size={32} />
                                        </div>
                                        <div className="media-body">
                                            <span className="d-block text-dark">Twitter</span>
                                            <small className="d-block text-secondary">@{speaker.twitter}</small>
                                        </div>
                                    </OutboundLink>}
                                    {/* End Social Profiles */}
                                    {/* Social Profiles */}
                                    {speaker.facebook && <OutboundLink className="media mb-4" eventLabel={`${speaker.name} - Facebook`} to={`https://facebook.com/${speaker.facebook}`} rel="nofollow noopener" target="_blank">
                                        <div className="u-sm-avatar mr-3">
                                            <Facebook color="black" size={32} />
                                        </div>
                                        <div className="media-body">
                                            <span className="d-block text-dark">Facebook</span>
                                            <small className="d-block text-secondary">{speaker.facebook}</small>
                                        </div>
                                    </OutboundLink>}
                                    {/* End Social Profiles */}
                                </div>
                            </div>
                            {/* End Social Profiles */}
                        </div>
                        <div className="col-lg-9">
                            {/* User Details */}
                            <div className="mb-4">
                                <h1 className="h4">{speaker.name}</h1>
                                <h4 className="h6 text-secondary mb-0">{speaker.location}</h4>
                            </div>
                            {/* End User Details */}
                            {/* Info */}
                            <div className="mb-4" dangerouslySetInnerHTML={{__html: speaker.resume}}></div>
                            {/* End Info */}
                            {/* Collections */}
                            <ul className="list-inline d-flex align-items-center">
                                <li className="list-inline-item mb-3 mb-sm-0 mr-5">
                                    <div className="d-flex align-items-center">
                                        <span className="bg-warning text-white font-weight-medium rounded py-2 px-3 mr-2">{speaker.events.edges.length}</span>
                                        <div className="text-secondary">Event{speaker.events.edges.length > 1 && 's'}</div>
                                    </div>
                                </li>
                                <li className="list-inline-item mb-3 mb-sm-0">
                                    <div className="d-flex align-items-center">
                                        <span className="bg-success text-white font-weight-medium rounded py-2 px-3 mr-2" ><UserCheck /></span>
                                        <div className="text-secondary">Verified</div>
                                    </div>
                                </li>
                            </ul>
                            {/* End Collections */}
                            <hr className="my-7" />
                            {/* Info */}
                            <h3 className="h4 mb-3" id="events">Event{speaker.events.edges.length > 1 && 's'}</h3>
                            {speaker.events.edges.map(edge => (
                                <a href={`/events/${edge.node.slug}`}>
                                {/* <div className="row mb-7"> */}
                                    {/* <div className="col-md-12 mb-7 mb-md-0"> */}
                                        {/* Info */}
                                        <div className="d-flex mb-5">
                                            <img className="align-self-center mr-4" src={edge.node.image.imagePreview} alt={edge.node.name} width={100} />
                                            <div>
                                                <small className="d-block text-secondary">{edge.node.date}</small>
                                                <h4 className="h6 mb-0">{edge.node.name}</h4>
                                                <span className="d-block text-secondary">in {edge.node.place}</span>
                                                <span className="d-block text-secondary">as {edge.bio}</span>
                                            </div>
                                        </div>
                                        {/* End Info */}
                                    {/* </div> */}
                                {/* </div> */}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};
export default SpeakerDetail;
